<template>
  <div class="min-h-screen" :style="pageStyle">
    <div v-if="loading" class="flex min-h-screen items-center justify-center">
      <div class="h-12 w-12 animate-spin rounded-full border-4 border-gray-200 border-t-[var(--accent)]"></div>
    </div>

    <div v-else-if="error" class="flex min-h-screen items-center justify-center px-4">
      <div class="max-w-md space-y-3 text-center">
        <h1 class="text-3xl font-semibold text-gray-900">Status page not found</h1>
        <p class="text-gray-600">The requested status page could not be found.</p>
      </div>
    </div>

    <div v-else-if="statusPage" class="mx-auto max-w-6xl space-y-10 px-4 py-10">
      <header class="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div class="flex items-center gap-3">
          <div class="flex h-14 w-14 items-center justify-center overflow-hidden rounded-2xl border-2 border-gray-900 bg-white shadow-sm">
            <img
              v-if="statusPage.branding?.logoUrl"
              :src="statusPage.branding.logoUrl"
              :alt="statusPage.name"
              class="h-full w-full object-contain p-2"
            />
            <span v-else class="text-lg font-bold text-gray-900">{{ statusPage.name?.slice(0, 2)?.toUpperCase() }}</span>
          </div>
          <div>
            <p class="text-xs uppercase tracking-[0.2em] text-gray-500">Status</p>
            <h1 class="text-3xl font-bold leading-tight text-gray-900">{{ statusPage.name }}</h1>
            <p v-if="statusPage.description" class="mt-1 max-w-2xl text-sm text-gray-600">{{ statusPage.description }}</p>
          </div>
        </div>

        <div class="flex flex-wrap items-center gap-3">
          <button
            type="button"
            class="inline-flex items-center gap-2 rounded-full border-2 border-gray-900 bg-white px-4 py-2 text-sm font-semibold shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg"
            @click="scrollToSubscribe"
          >
            <span class="inline-block h-2 w-2 rounded-full bg-[var(--accent)]"></span>
            Subscribe
          </button>
    <!--       <div class="flex items-center gap-2 rounded-full border border-gray-200 bg-white/70 px-3 py-2 text-xs text-gray-600">
            <span class="inline-block h-2 w-2 rounded-full bg-emerald-500"></span>
            Last updated {{ formatDate(statusPage.lastUpdated) }}
          </div> -->
        </div>
      </header>

      <section class="space-y-6">
        <div class="flex flex-col items-center gap-4 rounded-2xl border-2 border-gray-900 bg-white/90 p-8 text-center shadow-lg">
          <div class="relative">
            <div class="absolute inset-0 animate-ping rounded-full opacity-30" :style="{ backgroundColor: overallStatusMeta.bg }"></div>
            <div class="relative flex h-16 w-16 items-center justify-center rounded-full text-white" :style="{ backgroundColor: overallStatusMeta.bg }">
              <svg viewBox="0 0 24 24" class="h-8 w-8">
                <path fill="currentColor" d="M9.75 16.5 5.5 12.25l1.5-1.5 2.75 2.75 7.25-7.25 1.5 1.5Z" />
              </svg>
            </div>
          </div>
          <div>
            <p class="text-xs font-semibold uppercase tracking-[0.2em] text-gray-500">Current status</p>
            <h2 class="text-3xl font-semibold text-gray-900">{{ overallStatusMeta.label }}</h2>
            <p class="text-sm text-gray-600">{{ overallStatusMeta.description }}</p>
            <!-- <p class="mt-1 text-xs text-gray-500">Last updated {{ formatDate(statusPage.lastUpdated) }}</p> -->
          </div>
        </div>

        <div class="grid gap-6 md:grid-cols-[2fr,1fr]">
          <div class="rounded-2xl border-2 border-dashed border-gray-900 bg-white/80 p-5 shadow-sm">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-xs font-semibold uppercase tracking-wide text-gray-500">Availability trend</p>
                <p class="text-sm text-gray-600">{{ historyRangeTitle }}</p>
              </div>
              <span class="rounded-full bg-[var(--accent)]/10 px-3 py-1 text-xs font-semibold text-[var(--accent)]">Live</span>
            </div>
            <div class="mt-4 flex h-28 items-end gap-1">
              <div
                v-for="(point, idx) in overallHistory"
                :key="`${point.date}-${idx}`"
                class="h-full flex-1 rounded-full transition-transform hover:-translate-y-1"
                :class="historyBarClass(point.uptime)"
                :style="{ height: `${point.uptime === null ? 6 : Math.max(point.uptime * 100, 6)}%` }"
                :title="point.uptime === null ? 'No data' : `${formatDate(point.date)} • ${(point.uptime * 100).toFixed(2)}% uptime`"
              ></div>
              <div v-if="historyLoading" class="flex h-full flex-1 items-center justify-center rounded bg-gray-100 text-xs text-gray-500">
                Loading…
              </div>
            </div>
            <div class="mt-3 flex items-center justify-between text-xs text-gray-500">
              <span>{{ historyRangeLabel.start }}</span>
              <span>{{ historyRangeLabel.end }}</span>
            </div>
            <p v-if="historyError" class="mt-2 text-xs font-semibold text-red-600">Unable to load history right now.</p>
          </div>

          <div class="rounded-2xl border-2 border-gray-900 bg-white/90 p-5 shadow-lg">
            <h3 class="text-sm font-semibold uppercase tracking-wide text-gray-600">Snapshot</h3>
            <div class="mt-4 space-y-3">
              <div class="flex items-center justify-between rounded-xl border border-gray-200 bg-gray-50/70 px-3 py-2">
                <span class="text-sm text-gray-600">Availability (avg)</span>
                <span class="text-base font-semibold text-gray-900">
                  {{ averageUptime !== null ? `${averageUptime.toFixed(2)}%` : '—' }}
                </span>
              </div>
              <div class="flex items-center justify-between rounded-xl border border-gray-200 bg-gray-50/70 px-3 py-2">
                <span class="text-sm text-gray-600">Components operational</span>
                <span class="text-base font-semibold text-gray-900">{{ componentHealth.healthy }}/{{ componentHealth.total }}</span>
              </div>
              <div class="flex items-center justify-between rounded-xl border border-gray-200 bg-gray-50/70 px-3 py-2">
                <span class="text-sm text-gray-600">Active incidents</span>
                <span class="text-base font-semibold text-gray-900">{{ statusPage.activeIncidents?.length ?? 0 }}</span>
              </div>
              <div class="flex items-center justify-between rounded-xl border border-gray-200 bg-gray-50/70 px-3 py-2">
                <span class="text-sm text-gray-600">Scheduled maintenance</span>
                <span class="text-base font-semibold text-gray-900">{{ statusPage.scheduledMaintenances?.length ?? 0 }}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section v-if="statusPage.branding?.customHtml" class="rounded-2xl border border-gray-200 bg-white/80 p-6 shadow-sm">
        <div v-html="statusPage.branding.customHtml" class="prose max-w-none"></div>
      </section>

      <section class="space-y-4" id="components">
        <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 class="text-2xl font-semibold text-gray-900">Service status</h2>
            <p class="text-sm text-gray-600">Component health over {{ historyRangeInline }}</p>
          </div>
          <div class="flex flex-wrap items-center gap-3">
            <select
              class="sm:hidden rounded-full border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 shadow-sm"
              :value="selectedRangeDays"
              @change="onRangeSelectChange"
            >
              <option v-for="option in historyRangeOptions" :key="option.label" :value="option.days">
                {{ option.label }}
              </option>
            </select>
            <div class="hidden sm:inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-1.5 py-1 text-xs font-semibold text-gray-600 shadow-sm">
              <button
                v-for="option in historyRangeOptions"
                :key="option.label"
                type="button"
                class="rounded-full px-3 py-1 transition"
                :class="selectedRangeDays === option.days ? 'bg-[var(--accent)] text-white shadow' : 'text-gray-600 hover:bg-gray-100'"
                :aria-pressed="selectedRangeDays === option.days"
                @click="selectHistoryRange(option.days)"
              >
                {{ option.label }}
              </button>
            </div>
            <span class="rounded-full bg-[var(--accent)]/10 px-3 py-1 text-xs font-semibold text-[var(--accent)]">
              {{ componentHealth.total }} services
            </span>
          </div>
        </div>
        <div class="flex flex-wrap items-center gap-2">
          <span
            class="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold shadow-sm"
            :class="componentStatusClass(statusPage.overallStatus)"
          >
            <span class="h-2.5 w-2.5 rounded-full" :class="componentStatusDot(statusPage.overallStatus)"></span>
            <span>{{ overallStatusMeta.label }}</span>
            <span v-if="overallUptime30dLabel" class="text-current">
              • All services {{ overallUptime30dLabel }} (30d)
            </span>
          </span>
        </div>
        <div class="flex flex-wrap items-center gap-4 text-xs text-gray-500">
          <div class="flex items-center gap-2">
            <span class="h-2 w-2 rounded-full bg-emerald-500"></span>
            99.95–100% uptime
          </div>
          <div class="flex items-center gap-2">
            <span class="h-2 w-2 rounded-full bg-amber-400"></span>
            99–99.95% uptime or avg latency &gt; {{ latencyWarningMs }}ms
          </div>
          <div class="flex items-center gap-2">
            <span class="h-2 w-2 rounded-full bg-orange-500"></span>
            95–99% uptime
          </div>
          <div class="flex items-center gap-2">
            <span class="h-2 w-2 rounded-full bg-red-500"></span>
            &lt;95% uptime or full outage
          </div>
          <div class="flex items-center gap-2">
            <span class="h-2 w-2 rounded-full bg-gray-300"></span>
            No data / maintenance
          </div>
          <div class="flex items-center gap-2">
            <span class="h-0.5 w-4 rounded-full bg-red-500"></span>
            Incident window
          </div>
        </div>

        <div v-if="groupedComponents.length === 0" class="rounded-xl border-2 border-dashed border-gray-200 bg-white/70 px-6 py-10 text-center text-gray-500">
          No services are being monitored yet.
        </div>

        <div v-else class="space-y-10">
          <section
            v-for="group in groupedComponents"
            :key="group.key"
            class="space-y-6"
          >
            <div v-if="group.label || showUngroupedHeader" class="flex flex-wrap items-center justify-between gap-2">
              <div class="flex items-center gap-3">
                <h3 class="text-base font-semibold text-gray-900">
                  {{ group.label || 'Other services' }}
                </h3>
                <span class="text-xs text-gray-500">{{ group.components.length }} services</span>
              </div>
              <div class="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                <span class="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-2 py-1 text-[11px] font-semibold text-gray-700">
                  <span class="h-2 w-2 rounded-full" :class="componentStatusDot(getGroupStatus(group))"></span>
                  {{ componentStatusText(getGroupStatus(group)) }}
                </span>
                <span v-if="getGroupUptimeLabel(group)" class="text-[11px] font-semibold text-gray-600">
                  {{ getGroupUptimeLabel(group) }}
                </span>
                <button
                  type="button"
                  class="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-2 py-1 text-[11px] font-semibold text-gray-600 transition hover:text-gray-800"
                  :aria-expanded="!isGroupCollapsed(group.key)"
                  @click="toggleGroup(group.key)"
                >
                  <span>{{ isGroupCollapsed(group.key) ? 'Expand' : 'Collapse' }}</span>
                  <svg viewBox="0 0 24 24" class="h-3.5 w-3.5 transition" :class="isGroupCollapsed(group.key) ? '' : 'rotate-180'">
                    <path fill="currentColor" d="m12 15.5-6-6 1.5-1.5L12 12.5l4.5-4.5 1.5 1.5Z" />
                  </svg>
                </button>
              </div>
            </div>

            <div v-if="!isGroupCollapsed(group.key)" class="space-y-6">
              <article
                v-for="component in group.components"
                :key="component.id"
                class="rounded-2xl border border-gray-200/80 bg-white p-6 shadow-sm ring-1 ring-gray-100/80"
              >
                <div class="flex flex-col gap-4">
                  <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <div class="flex flex-wrap items-center gap-3">
                        <h3 class="text-lg font-semibold text-gray-900">{{ component.name }}</h3>
                      </div>
                      <p v-if="component.description" class="mt-1 max-w-3xl text-sm text-gray-600">
                        {{ component.description }}
                      </p>
                    </div>
                    <div class="flex items-center gap-2">
                      <span
                        class="inline-flex items-center gap-2 rounded-full px-4 py-2 text-base font-semibold shadow-sm"
                        :class="componentStatusClass(component.status)"
                      >
                        <span class="h-2.5 w-2.5 rounded-full" :class="componentStatusDot(component.status)"></span>
                        <span>{{ componentStatusText(component.status) }}</span>
                        <span v-if="getComponentUptimeBadge(component)" class="text-sm font-semibold text-gray-800">
                          • {{ getComponentUptimeBadge(component) }}
                        </span>
                        <span v-if="getComponentResponseTime24h(component) !== null" class="text-sm font-semibold text-gray-700">
                          • {{ formatResponseTime(getComponentResponseTime24h(component)) }} avg
                        </span>
                      </span>
                    </div>
                  </div>

                <div class="space-y-3">
                  <div class="relative">
                    <div class="relative overflow-x-auto pb-1">
                      <div class="relative min-w-full" :style="getHistoryBarStyle(component.id)">
                        <div class="flex h-3 items-center gap-0.5 rounded-full bg-gray-50/30 px-2 py-1">
                          <div
                            v-for="(point, idx) in getComponentHistory(component.id)"
                            :key="`${component.id}-${idx}`"
                            class="h-full flex-1 rounded-sm"
                            :class="historyBarClass(point.uptime, point.avgResponseTimeMs)"
                            :title="formatHistoryTooltip(point)"
                          ></div>
                        </div>
                        <div
                          class="pointer-events-none absolute left-2 right-2 -top-1 grid h-2"
                          :style="{ gridTemplateColumns: `repeat(${getComponentHistory(component.id).length}, minmax(0, 1fr))` }"
                        >
                          <span
                            v-for="(range, idx) in getIncidentRangesForComponent(component.id)"
                            :key="`incident-range-${component.id}-${idx}`"
                            class="h-full rounded-sm border border-red-400/30 bg-red-500/20"
                            :style="{ gridColumn: `${range.start + 1} / ${range.end + 2}` }"
                          ></span>
                        </div>
                        <div
                          class="absolute left-2 right-2 -top-3 grid h-3"
                          :style="{ gridTemplateColumns: `repeat(${getComponentHistory(component.id).length}, minmax(0, 1fr))` }"
                        >
                          <span
                            v-for="(range, idx) in getIncidentRangesForComponent(component.id)"
                            :key="`incident-dot-${component.id}-${idx}`"
                            class="h-2 w-2 justify-self-center rounded-full bg-red-500 shadow"
                            :style="{ gridColumn: `${range.start + 1} / span 1` }"
                          ></span>
                        </div>
                      </div>
                    </div>
                    <div v-if="isHistoryScrollable" class="pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-white to-transparent"></div>
                    <div v-if="isHistoryScrollable" class="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-white to-transparent"></div>
                    <a
                      v-if="getIncidentCountForComponent(component.id) > 1"
                      href="#history"
                      class="absolute -top-7 right-0 rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-700"
                    >
                      {{ getIncidentCountForComponent(component.id) }} incidents
                    </a>
                    <div v-if="historyLoading" class="absolute inset-0 flex items-center justify-center text-[11px] text-gray-500">
                      Loading…
                    </div>
                  </div>

                  <div class="flex items-center justify-between text-[11px] text-gray-500">
                    <span>{{ historyRangeLabel.start }}</span>
                    <span>{{ historyRangeLabel.end }}</span>
                  </div>

                  <div class="flex flex-wrap items-center gap-4 text-xs text-gray-600">
                    <div class="flex items-center gap-2">
                      <span class="text-[10px] font-semibold uppercase tracking-wide text-gray-400">7d</span>
                      <span :class="isNoDataUptime(component, 7) ? 'font-semibold text-gray-400' : 'font-semibold text-gray-900'">
                        {{ formatUptime(getComponentUptimeRolling(component, 7), '—') }}
                      </span>
                      <span v-if="showNoDataMessage(component) && !historyLoading" class="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-500">
                        No data
                      </span>
                    </div>
                    <div class="flex items-center gap-2">
                      <span class="text-[10px] font-semibold uppercase tracking-wide text-gray-400">30d</span>
                      <span :class="isNoDataUptime(component, 30) ? 'font-semibold text-gray-400' : 'font-semibold text-gray-900'">
                        {{ formatUptime(getComponentUptimeRolling(component, 30), '—') }}
                      </span>
                    </div>
                    <div class="flex items-center gap-2">
                      <span class="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Incidents</span>
                      <span class="font-semibold text-gray-900">{{ formatIncidentCount(getComponentIncidents30d(component)) }}</span>
                      <span class="text-[10px] text-gray-400">(30d)</span>
                      <a
                        v-if="(getComponentIncidents30d(component) ?? 0) > 0"
                        href="#history"
                        class="ml-1 font-semibold text-[var(--accent)] underline-offset-2 hover:underline"
                      >
                        View
                      </a>
                    </div>
                  </div>

                  <div class="rounded-xl bg-gray-50/70 px-3 py-2">
                    <div class="flex items-center justify-between text-[11px] text-gray-500">
                      <span>Avg response time (per day)</span>
                      <span v-if="getComponentResponseTime24h(component) !== null">
                        {{ formatResponseTime(getComponentResponseTime24h(component)) }} 24h avg
                      </span>
                    </div>
                    <div v-if="getLatencySeries(component.id).hasData" class="mt-2 grid grid-cols-[1fr,auto] items-end gap-2">
                      <div class="relative group">
                        <div
                          v-if="getLatencySeries(component.id).peak"
                          class="absolute right-0 top-0 -translate-y-full text-[10px] text-gray-400 opacity-0 transition group-hover:opacity-100"
                        >
                          {{ formatLatencyPeakLabel(getLatencySeries(component.id).peak) }}
                        </div>
                        <svg
                          viewBox="0 0 100 24"
                          class="h-5 w-full cursor-pointer"
                          preserveAspectRatio="none"
                        >
                          <line x1="0" y1="3" x2="100" y2="3" stroke="#e2e8f0" stroke-width="0.6" />
                          <line x1="0" y1="21" x2="100" y2="21" stroke="#e2e8f0" stroke-width="0.6" />
                          <line
                            x1="0"
                            :y1="getLatencyBaselineY()"
                            x2="100"
                            :y2="getLatencyBaselineY()"
                            stroke="#e2e8f0"
                            stroke-width="0.7"
                            stroke-dasharray="3 2"
                          />
                          <path
                            v-for="(segment, idx) in getLatencySeries(component.id).segments"
                            :key="`latency-${component.id}-${idx}`"
                            :d="segment.d"
                            fill="none"
                            :stroke="segment.color"
                            stroke-width="2.6"
                            stroke-linecap="round"
                          />
                          <circle
                            v-for="point in getLatencySeries(component.id).points"
                            :key="`latency-point-${component.id}-${point.index}`"
                            :cx="point.x"
                            :cy="point.y"
                            r="4"
                            fill="transparent"
                            stroke="transparent"
                            class="pointer-events-auto"
                          >
                            <title>{{ formatLatencyTooltip(point, component) }}</title>
                          </circle>
                          <circle
                            v-for="point in getLatencySeries(component.id).spikes"
                            :key="`latency-spike-${component.id}-${point.index}`"
                            :cx="point.x"
                            :cy="point.y"
                            r="2.4"
                            :fill="point.color"
                            stroke="#ffffff"
                            stroke-width="0.6"
                            class="opacity-40 transition group-hover:opacity-100"
                          >
                            <title>{{ formatLatencyTooltip(point, component) }}</title>
                          </circle>
                        </svg>
                      </div>
                      <div class="flex h-5 flex-col items-end justify-between text-[10px] text-gray-400">
                        <span>{{ latencyAxisLabel }}</span>
                        <span>0ms</span>
                      </div>
                    </div>
                    <div v-else class="mt-2 text-[11px] text-gray-400">No latency data yet.</div>
                  </div>
                </div>
              </div>
              </article>
            </div>
          </section>
        </div>
      </section>

      <section v-if="statusPage.activeIncidents?.length" class="space-y-4" id="incidents">
        <div class="flex items-center justify-between">
          <h2 class="text-2xl font-semibold text-gray-900">Active incidents</h2>
          <span class="rounded-full bg-red-100 px-3 py-1 text-xs font-semibold text-red-700">
            {{ statusPage.activeIncidents.length }} open
          </span>
        </div>

        <div class="space-y-4">
          <article
            v-for="incident in statusPage.activeIncidents"
            :key="incident.id"
            class="rounded-2xl border-2 border-red-200 bg-red-50 p-5 shadow-sm"
          >
            <div class="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <div class="flex items-center gap-2">
                  <span class="inline-flex h-8 w-8 items-center justify-center rounded-full bg-red-100 text-red-700">
                    !
                  </span>
                  <div>
                    <h3 class="text-lg font-semibold text-red-900">{{ incident.title }}</h3>
                    <p class="text-xs uppercase tracking-wide text-red-700">{{ incident.severity }} impact</p>
                  </div>
                </div>
                <p v-if="incident.description" class="mt-2 text-sm text-red-800">
                  {{ incident.description }}
                </p>
                <p class="mt-1 text-xs text-red-700">
                  Started {{ formatDate(incident.createdAt) }} • {{ incident.status?.toString().replace('_', ' ') }}
                </p>
              </div>
            </div>

            <div v-if="incident.updates?.length" class="mt-4 space-y-3 border-l-4 border-red-200 pl-4">
              <div
                v-for="update in incident.updates.slice(0, 3)"
                :key="update.id"
                class="rounded-lg bg-white/60 px-3 py-2 shadow-sm"
              >
                <div class="flex items-center justify-between text-xs text-red-700">
                  <span class="font-semibold">{{ formatDate(update.createdAt) }}</span>
                  <span v-if="update.status" class="capitalize">{{ formatStatusLabel(update.status) }}</span>
                </div>
                <p class="mt-1 text-sm text-red-900">{{ update.message }}</p>
              </div>
            </div>
          </article>
        </div>
      </section>

      <section v-if="statusPage.scheduledMaintenances?.length" class="space-y-4" id="maintenance">
        <div class="flex items-center justify-between">
          <h2 class="text-2xl font-semibold text-gray-900">Scheduled maintenance</h2>
          <span class="rounded-full bg-indigo-100 px-3 py-1 text-xs font-semibold text-indigo-700">
            {{ statusPage.scheduledMaintenances.length }} planned
          </span>
        </div>

        <div class="space-y-3">
          <article
            v-for="maintenance in statusPage.scheduledMaintenances"
            :key="maintenance.id"
            class="rounded-2xl border-2 border-indigo-200 bg-indigo-50 p-5 shadow-sm"
          >
            <div class="flex items-center justify-between">
              <div>
                <h3 class="text-lg font-semibold text-indigo-900">{{ maintenance.title }}</h3>
                <p v-if="maintenance.description" class="mt-1 text-sm text-indigo-800">
                  {{ maintenance.description }}
                </p>
              </div>
              <span class="rounded-full bg-indigo-100 px-3 py-1 text-xs font-semibold text-indigo-700">
                {{ maintenance.status?.toString().replace('_', ' ') }}
              </span>
            </div>
            <p class="mt-2 text-sm text-indigo-700">
              {{ formatDate(maintenance.startTime) }} — {{ formatDate(maintenance.endTime) }}
            </p>
          </article>
        </div>
      </section>

      <section class="space-y-4" id="history">
        <div class="flex items-center justify-between">
          <div>
            <h2 class="text-2xl font-semibold text-gray-900">Past incidents</h2>
            <p class="text-sm text-gray-600">Recent history pulled automatically</p>
          </div>
          <button
            v-if="pastIncidentsHasMore"
            class="rounded-full border border-gray-300 bg-white px-4 py-2 text-xs font-semibold text-gray-700 transition hover:-translate-y-0.5 hover:shadow"
            :disabled="pastIncidentsLoading"
            @click="loadMorePastIncidents"
          >
            {{ pastIncidentsLoading ? 'Loading…' : 'Load more' }}
          </button>
        </div>

        <div v-if="pastIncidentsLoading && pastIncidents.length === 0" class="rounded-xl border border-gray-200 bg-white/70 px-6 py-6 text-center text-gray-500">
          Loading incident history…
        </div>

        <div v-else-if="pastIncidents.length === 0" class="rounded-xl border border-dashed border-gray-200 bg-white/70 px-6 py-10 text-center text-gray-500">
          No incident was recorded in the recent period.
        </div>

        <div v-else class="space-y-3">
          <article
            v-for="incident in pastIncidents"
            :key="incident.id"
            class="rounded-2xl border border-gray-200 bg-white/90 p-5 shadow-sm"
          >
            <div class="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <div class="flex items-center gap-2">
                  <div class="h-3 w-3 rounded-full" :class="incidentSeverityDot(incident.severity)"></div>
                  <h3 class="text-lg font-semibold text-gray-900">{{ incident.title }}</h3>
                </div>
                <p v-if="incident.description" class="mt-1 text-sm text-gray-700">{{ incident.description }}</p>
                <p class="text-xs text-gray-500">
                  {{ formatDate(incident.createdAt) }}
                  <span v-if="incident.resolvedAt"> • Resolved {{ formatDate(incident.resolvedAt) }}</span>
                </p>
              </div>
              <span class="rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700 capitalize">
                {{ incident.status?.toString().replace('_', ' ') }}
              </span>
            </div>

            <div v-if="incident.updates?.length" class="mt-3 space-y-2 border-l-4 border-gray-100 pl-4">
              <div
                v-for="update in incident.updates.slice(0, 3)"
                :key="update.id"
                class="rounded-lg bg-gray-50 px-3 py-2"
              >
                <div class="flex items-center justify-between text-[11px] text-gray-600">
                  <span class="font-semibold text-gray-800">{{ formatDate(update.createdAt) }}</span>
                  <span v-if="update.status" class="capitalize text-gray-600">{{ formatStatusLabel(update.status) }}</span>
                </div>
                <p class="mt-1 text-sm text-gray-700">{{ update.message }}</p>
              </div>
            </div>
          </article>
        </div>
      </section>

      <section ref="subscriptionSection" class="rounded-2xl border-2 border-gray-900 bg-white/90 p-6 shadow-lg">
        <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 class="text-2xl font-semibold text-gray-900">Get notified</h2>
            <p class="text-sm text-gray-600">Subscribe to incidents and maintenance updates.</p>
          </div>
          <form class="flex w-full max-w-md gap-2" @submit.prevent="subscribe">
            <input
              v-model="subscriptionEmail"
              type="email"
              required
              placeholder="you@example.com"
              class="w-full rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm shadow-sm focus:border-[var(--accent)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/30"
            />
            <!-- Honeypot field to deter bots -->
            <input v-model="honeypot" type="text" tabindex="-1" autocomplete="off" class="hidden" aria-hidden="true" />
            <button
              type="submit"
              :disabled="subscribing"
              class="whitespace-nowrap rounded-xl bg-[var(--accent)] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-60"
            >
              {{ subscribing ? 'Subscribing…' : 'Subscribe' }}
            </button>
          </form>
        </div>
      </section>

      <footer class="pb-6 pt-4 text-center text-xs text-gray-500">
        <p>Monitoring powered by PingAlert.IO</p>
      </footer>
    </div>

    <component
      v-if="statusPage?.branding?.customCss"
      :is="'style'"
      v-html="statusPage.branding.customCss"
    />

    <!-- Password Prompt -->
    <div v-if="showPasswordPrompt" class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
      <div class="w-full max-w-md space-y-4 rounded-2xl bg-white p-6 shadow-xl">
        <div class="flex items-center justify-between">
          <h3 class="text-lg font-semibold text-gray-900">Private status page</h3>
          <button @click="showPasswordPrompt = false" class="text-gray-500 hover:text-gray-700">
            <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <p class="text-sm text-gray-600">Enter the password to view this status page.</p>
        <form @submit.prevent="authenticate" class="space-y-3">
          <input
            v-model="password"
            type="password"
            required
            placeholder="Password"
            class="w-full rounded-xl border border-gray-300 px-4 py-2.5 text-sm shadow-sm focus:border-[var(--accent)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/30"
            :class="{ 'border-red-400 focus:ring-red-200': passwordError }"
          />
          <p v-if="passwordError" class="text-sm text-red-600">{{ passwordError }}</p>
          <div class="flex justify-end gap-2">
            <button type="button" class="rounded-xl border border-gray-200 px-4 py-2 text-sm text-gray-700" @click="showPasswordPrompt = false">
              Cancel
            </button>
            <button type="submit" :disabled="authenticating" class="rounded-xl bg-[var(--accent)] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
              {{ authenticating ? 'Authenticating…' : 'Access' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'

const defaultHistoryDays = 90
const maxHistoryDays = 365
const historyRangeOptions = [
  { label: '7 Days', days: 7 },
  { label: '30 Days', days: 30 },
  { label: '90 Days', days: 90 },
  { label: 'All', days: maxHistoryDays }
]
const route = useRoute()

const statusPage = ref<any>(null)
const historyData = ref<any>(null)
const selectedRangeDays = ref(defaultHistoryDays)
const loadedHistoryDays = ref(defaultHistoryDays)
const loading = ref(true)
const historyLoading = ref(false)
const historyError = ref(false)
const error = ref(false)
const subscriptionEmail = ref('')
const subscribing = ref(false)
const subscriptionSection = ref<HTMLElement | null>(null)
const pageSessionToken = ref<string | null>(null)
const subscriptionToken = ref<string | null>(null)
const honeypot = ref('')
const pageLoadStartedAt = Date.now()
const originalHost = window.location.host
//const subscriptionToken = ref<string | null>(null)

const pastIncidents = ref<any[]>([])
const pastIncidentsLoading = ref(false)
const pastIncidentsPage = ref(1)
const pastIncidentsHasMore = ref(true)

const showPasswordPrompt = ref(false)
const password = ref('')
const passwordError = ref('')
const authenticating = ref(false)

const pageStyle = computed(() => {
  const branding = statusPage.value?.branding
  return {
    backgroundColor: branding?.backgroundColor || '#FFFFFF',
    color: branding?.textColor || '#0f172a',
    '--accent': branding?.primaryColor || '#0f172a',
    '--muted': '#6b7280',
    '--card': 'rgba(255,255,255,0.9)'
  } as Record<string, string>
})

const historyRangeTitle = computed(() => {
  return selectedRangeDays.value >= maxHistoryDays
    ? 'All history'
    : `Last ${selectedRangeDays.value} days`
})

const historyRangeInline = computed(() => {
  return selectedRangeDays.value >= maxHistoryDays
    ? 'all history'
    : `last ${selectedRangeDays.value} days`
})

const historyScrollThreshold = 120
const historySegmentWidth = 8

const isHistoryScrollable = computed(() => selectedRangeDays.value >= historyScrollThreshold)

const collapsedGroups = ref<Record<string, boolean>>({})

const sortedComponents = computed(() => {
  if (!statusPage.value?.components) return []
  return [...statusPage.value.components].sort((a: any, b: any) => (a.order || 0) - (b.order || 0))
})

const groupedComponents = computed(() => {
  const groups: Array<{ key: string; label: string; components: typeof sortedComponents.value }> = []
  const indexByKey = new Map<string, number>()

  for (const component of sortedComponents.value) {
    const label = (component.group ?? '').trim()
    const key = label ? label.toLowerCase() : '__ungrouped'
    const existingIndex = indexByKey.get(key)

    if (existingIndex === undefined) {
      indexByKey.set(key, groups.length)
      groups.push({ key, label, components: [component] })
    } else {
      groups[existingIndex].components.push(component)
    }
  }

  return groups
})

const showUngroupedHeader = computed(() => {
  const hasNamedGroups = groupedComponents.value.some(group => group.label.length > 0)
  const hasUngrouped = groupedComponents.value.some(group => group.label.length === 0)
  return hasNamedGroups && hasUngrouped
})

const overallStatusMeta = computed(() => {
  const status = statusPage.value?.overallStatus
  const map: Record<number, { label: string; description: string; bg: string }> = {
    1: { label: 'All systems operational', description: 'Everything is running smoothly.', bg: '#16a34a' },
    2: { label: 'Minor issues', description: 'Some services may be degraded.', bg: '#fbbf24' },
    3: { label: 'Major issues', description: 'Service disruption detected.', bg: '#f97316' },
    4: { label: 'Critical outage', description: 'Multiple systems are impacted.', bg: '#ef4444' },
    5: { label: 'Under maintenance', description: 'Scheduled work is in progress.', bg: '#6366f1' }
  }
  return map[status as number] ?? map[1]
})

const componentHealth = computed(() => {
  const comps = statusPage.value?.components ?? []
  const unhealthy = [2, 3, 4, 5]
  const healthy = comps.filter((c: any) => !unhealthy.includes(c.status)).length
  return { healthy, total: comps.length }
})

const sortedHistoryData = computed(() => {
  if (!historyData.value?.data?.length) return []
  return [...historyData.value.data].sort(
    (a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime()
  )
})

const visibleHistoryData = computed(() => {
  const data = sortedHistoryData.value
  if (data.length === 0) return []
  const days = Math.min(selectedRangeDays.value, data.length)
  return data.slice(-days)
})

const averageUptime = computed(() => {
  const data = visibleHistoryData.value
  if (data.length === 0) return null
  let totalChecks = 0
  let failedChecks = 0
  for (const point of data) {
    for (const comp of point.components ?? []) {
      totalChecks += comp.totalChecks ?? 0
      failedChecks += comp.failedChecks ?? 0
    }
  }
  if (totalChecks === 0) return null
  const success = Math.max(0, totalChecks - failedChecks)
  return (success / totalChecks) * 100
})

const overallUptime30dLabel = computed(() => {
  const value = getOverallUptimeRolling(30)
  if (value === null) return ''
  return `${(value * 100).toFixed(2)}%`
})

const overallHistory = computed(() => {
  if (visibleHistoryData.value.length === 0) return []
  return visibleHistoryData.value.map((point: any) => {
    const components = point.components ?? []
    let totalChecks = 0
    let failedChecks = 0
    for (const comp of components) {
      totalChecks += comp.totalChecks ?? 0
      failedChecks += comp.failedChecks ?? 0
    }
    const uptime = totalChecks > 0
      ? Math.max(0, totalChecks - failedChecks) / totalChecks
      : (typeof point.overallUptime === 'number' && point.overallUptime >= 0 ? point.overallUptime : null)

    return {
      date: point.date,
      uptime,
      incidents: point.incidents?.length ?? 0
    }
  })
})

function getOverallUptimeRolling(days: number): number | null {
  const data = sortedHistoryData.value
  if (data.length === 0) return null
  const slice = data.slice(-Math.min(days, data.length))
  let totalChecks = 0
  let failedChecks = 0
  for (const point of slice) {
    for (const comp of point.components ?? []) {
      totalChecks += comp.totalChecks ?? 0
      failedChecks += comp.failedChecks ?? 0
    }
  }
  if (totalChecks === 0) return null
  const success = Math.max(0, totalChecks - failedChecks)
  return success / totalChecks
}

type ComponentHistoryPoint = {
  date: string
  uptime: number | null
  incidents: number
  avgResponseTimeMs: number | null
  maxResponseTimeMs: number | null
  totalChecks: number
  failedChecks: number
}
type ComponentHistoryDisplayPoint = ComponentHistoryPoint

const componentHistoryMap = computed(() => {
  const map = new Map<string, ComponentHistoryPoint[]>()
  if (!sortedHistoryData.value.length) return map

  for (const point of sortedHistoryData.value) {
    for (const comp of point.components ?? []) {
      const arr = map.get(comp.componentId) ?? []
      const uptimeValue = typeof comp.uptime === 'number' ? comp.uptime : null
      arr.push({
        date: point.date,
        uptime: uptimeValue !== null && uptimeValue < 0 ? null : uptimeValue,
        incidents: comp.incidents ?? 0,
        avgResponseTimeMs: typeof comp.avgResponseTimeMs === 'number' ? comp.avgResponseTimeMs : null,
        maxResponseTimeMs: typeof comp.maxResponseTimeMs === 'number' ? comp.maxResponseTimeMs : null,
        totalChecks: comp.totalChecks ?? 0,
        failedChecks: comp.failedChecks ?? 0
      })
      map.set(comp.componentId, arr)
    }
  }

  for (const [key, arr] of map.entries()) {
    arr.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    map.set(key, arr)
  }

  return map
})

const visibleComponentHistoryMap = computed(() => {
  const map = new Map<string, ComponentHistoryPoint[]>()
  for (const [key, entries] of componentHistoryMap.value.entries()) {
    const days = Math.min(selectedRangeDays.value, entries.length)
    map.set(key, entries.slice(-days))
  }
  return map
})

const historyRangeLabel = computed(() => {
  const data = visibleHistoryData.value
  if (!data || data.length === 0) return { start: '—', end: '—' }
  const sorted = [...data]
  return {
    start: formatDate(sorted[0].date),
    end: formatDate(sorted[sorted.length - 1].date)
  }
})

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

function formatDateShort(dateString: string) {
  return new Date(dateString).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric'
  })
}

function componentStatusClass(status: number) {
  switch (status) {
    case 1:
      return 'bg-emerald-100 text-emerald-800'
    case 2:
      return 'bg-amber-100 text-amber-800'
    case 3:
      return 'bg-orange-100 text-orange-800'
    case 4:
      return 'bg-red-100 text-red-800'
    case 5:
      return 'bg-indigo-100 text-indigo-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

function componentStatusDot(status: number) {
  switch (status) {
    case 1:
      return 'bg-emerald-500'
    case 2:
      return 'bg-amber-400'
    case 3:
      return 'bg-orange-500'
    case 4:
      return 'bg-red-500'
    case 5:
      return 'bg-indigo-500'
    default:
      return 'bg-gray-400'
  }
}

function componentStatusText(status: number) {
  switch (status) {
    case 1:
      return 'Operational'
    case 2:
      return 'Degraded'
    case 3:
      return 'Partial outage'
    case 4:
      return 'Major outage'
    case 5:
      return 'Maintenance'
    default:
      return 'Unknown'
  }
}

function isGroupCollapsed(groupKey: string) {
  return Boolean(collapsedGroups.value[groupKey])
}

function toggleGroup(groupKey: string) {
  collapsedGroups.value[groupKey] = !collapsedGroups.value[groupKey]
}

function getGroupStatus(group: any) {
  const statuses = (group?.components ?? [])
    .map((component: any) => component.status)
    .filter((status: any) => typeof status === 'number')
  if (statuses.length === 0) return 1
  return Math.max(...statuses)
}

function getGroupUptimeRolling(group: any, days: number): number | null {
  let totalChecks = 0
  let failedChecks = 0
  for (const component of group?.components ?? []) {
    const entries = getHistoryWindow(component.id, days)
    for (const entry of entries) {
      totalChecks += entry.totalChecks
      failedChecks += entry.failedChecks
    }
  }
  if (totalChecks === 0) return null
  const success = Math.max(0, totalChecks - failedChecks)
  return success / totalChecks
}

function getGroupUptimeLabel(group: any) {
  const uptime = getGroupUptimeRolling(group, 30)
  if (uptime === null) return ''
  return `${formatUptime(uptime)} (30d)`
}

const latencyWarningMs = 1500
const latencyCriticalMs = 2000
const latencyAxisMax = 2000
const latencyBaselineMs = 500
const latencyChartHeight = 24
const latencyChartPadding = 3

function historyBarClass(uptime: number | null, avgResponseTimeMs?: number | null) {
  if (uptime === null) return 'bg-gray-200/40'
  if (uptime < 0.95) return 'bg-red-500'
  if (uptime < 0.99) return 'bg-orange-500'
  if (typeof avgResponseTimeMs === 'number' && avgResponseTimeMs >= latencyWarningMs) return 'bg-amber-400'
  if (uptime < 0.9995) return 'bg-amber-400'
  return 'bg-emerald-500'
}

function getComponentHistory(componentId: string): ComponentHistoryDisplayPoint[] {
  const historyEntries = visibleComponentHistoryMap.value.get(componentId)
  if (historyEntries && historyEntries.length > 0) {
    return historyEntries
  }

  const days = Math.max(1, selectedRangeDays.value)
  return Array.from({ length: days }).map((_, idx) => ({
    date: new Date(Date.now() - (days - 1 - idx) * 24 * 60 * 60 * 1000).toISOString(),
    uptime: null,
    incidents: 0,
    avgResponseTimeMs: null,
    maxResponseTimeMs: null,
    totalChecks: 0,
    failedChecks: 0
  }))
}

function getHistoryBarStyle(componentId: string) {
  if (!isHistoryScrollable.value) return {}
  const length = getComponentHistory(componentId).length
  const minWidth = Math.max(length * historySegmentWidth, 360)
  return { minWidth: `${minWidth}px` }
}

function getIncidentCountForComponent(componentId: string) {
  return getComponentHistory(componentId).reduce((sum, entry) => sum + entry.incidents, 0)
}

function getHistoryWindow(componentId: string, days: number) {
  const entries = componentHistoryMap.value.get(componentId)
  if (!entries || entries.length === 0) return []
  return entries.slice(-Math.min(days, entries.length))
}

function showNoDataMessage(component: any) {
  const historyEntries = getHistoryWindow(component.id, 7)
  if (historyEntries.length > 0) {
    const hasChecks = historyEntries.some(entry => entry.totalChecks > 0)
    const hasUptime = historyEntries.some(entry => entry.uptime !== null)
    return !(hasChecks || hasUptime)
  }
  const lastCheck = component?.stats?.lastCheck
  if (!lastCheck) return true
  const lastCheckTime = new Date(lastCheck).getTime()
  const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000
  return Number.isNaN(lastCheckTime) || lastCheckTime < cutoff
}

type IncidentRange = { start: number; end: number }

function getIncidentRanges(entries: ComponentHistoryDisplayPoint[]): IncidentRange[] {
  const ranges: IncidentRange[] = []
  let start = -1

  entries.forEach((entry, idx) => {
    if (entry.incidents > 0) {
      if (start < 0) start = idx
    } else if (start >= 0) {
      ranges.push({ start, end: idx - 1 })
      start = -1
    }
  })

  if (start >= 0) {
    ranges.push({ start, end: entries.length - 1 })
  }

  return ranges
}

function getIncidentRangesForComponent(componentId: string) {
  return getIncidentRanges(getComponentHistory(componentId))
}

function computeTimeWeightedUptime(entries: ComponentHistoryDisplayPoint[]): number | null {
  let totalChecks = 0
  let failedChecks = 0
  for (const entry of entries) {
    if (entry.totalChecks > 0) {
      totalChecks += entry.totalChecks
      failedChecks += entry.failedChecks
    }
  }
  if (totalChecks === 0) return null
  const success = Math.max(0, totalChecks - failedChecks)
  return success / totalChecks
}

function computeAverageUptime(entries: ComponentHistoryDisplayPoint[]): number | null {
  const samples = entries
    .map(entry => entry.uptime)
    .filter((uptime): uptime is number => typeof uptime === 'number')
  if (samples.length === 0) return null
  const sum = samples.reduce((acc, uptime) => acc + uptime, 0)
  return sum / samples.length
}

function getComponentUptimeRolling(component: any, days: number): number | null {
  const historyEntries = getHistoryWindow(component.id, days)
  if (historyEntries.length > 0) {
    const weighted = computeTimeWeightedUptime(historyEntries)
    if (weighted !== null) return weighted
    const averaged = computeAverageUptime(historyEntries)
    if (averaged !== null) return averaged
    return null
  }

  const stats = component?.stats
  const fallback = days <= 7 ? stats?.uptime7d : stats?.uptime30d
  if (typeof fallback !== 'number') return null
  if (!stats?.lastCheck) return null

  const lastCheck = new Date(stats.lastCheck).getTime()
  const cutoff = Date.now() - days * 24 * 60 * 60 * 1000
  if (Number.isNaN(lastCheck) || lastCheck < cutoff) return null
  return fallback
}

function isNoDataUptime(component: any, days: number) {
  return getComponentUptimeRolling(component, days) === null
}

function getComponentUptimeBadge(component: any) {
  const uptime = getComponentUptimeRolling(component, 30)
  const label = formatUptime(uptime, 'No data (30d)')
  if (label === 'No data (30d)') return label
  return `${label} (30d)`
}

function getComponentResponseTime24h(component: any): number | null {
  const response = component?.stats?.avgResponseTime24h
  if (typeof response !== 'number') return null
  return response > 0 ? response : null
}

function getComponentIncidents30d(component: any): number | null {
  const historyEntries = getHistoryWindow(component.id, 30)
  if (historyEntries.length > 0) {
    return historyEntries.reduce((acc, entry) => acc + entry.incidents, 0)
  }

  const count = component?.stats?.incidentCount30d
  return typeof count === 'number' ? count : null
}

type LatencySeverity = 'normal' | 'warning' | 'critical'

type LatencyPoint = {
  index: number
  x: number
  y: number
  value: number
  date: string
  maxResponseTimeMs: number | null
  severity: LatencySeverity
  color: string
}

type LatencySegment = {
  d: string
  color: string
}

type LatencyPeak = {
  value: number
  date: string
}

type LatencySeries = {
  hasData: boolean
  segments: LatencySegment[]
  points: LatencyPoint[]
  spikes: LatencyPoint[]
  peak: LatencyPeak | null
}

const latencyAxisLabel = computed(() => formatLatencyAxisLabel(latencyAxisMax))

function getLatencyY(value: number) {
  const clamped = Math.min(Math.max(value, 0), latencyAxisMax)
  const usable = latencyChartHeight - latencyChartPadding * 2
  return latencyChartPadding + (1 - clamped / (latencyAxisMax || 1)) * usable
}

function getLatencyBaselineY() {
  return getLatencyY(latencyBaselineMs)
}

function getLatencyColor(value: number) {
  if (value >= latencyCriticalMs) return '#ef4444'
  if (value >= latencyWarningMs) return '#f59e0b'
  return '#16a34a'
}

function getLatencySeverity(value: number): LatencySeverity {
  if (value >= latencyCriticalMs) return 'critical'
  if (value >= latencyWarningMs) return 'warning'
  return 'normal'
}

function buildLatencySeries(entries: ComponentHistoryDisplayPoint[]): LatencySeries {
  if (entries.length < 2) return { hasData: false, segments: [], points: [], spikes: [], peak: null }
  const values = entries.map(entry => entry.avgResponseTimeMs)
  const valid = values.filter((value): value is number => typeof value === 'number' && value > 0)
  if (valid.length < 2) return { hasData: false, segments: [], points: [], spikes: [], peak: null }

  const step = values.length > 1 ? 100 / (values.length - 1) : 100
  const segments: LatencySegment[] = []
  const points: LatencyPoint[] = []
  const spikes: LatencyPoint[] = []

  let peak: LatencyPeak | null = null

  values.forEach((value, idx) => {
    if (typeof value === 'number' && value > 0) {
      const y = getLatencyY(value)
      const x = idx * step
      const severity = getLatencySeverity(value)
      const color = getLatencyColor(value)
      const point = {
        index: idx,
        x,
        y,
        value,
        date: entries[idx].date,
        maxResponseTimeMs: entries[idx].maxResponseTimeMs ?? null,
        severity,
        color
      }
      points.push(point)
      if (severity !== 'normal') {
        spikes.push(point)
      }
      if (!peak || value > peak.value) {
        peak = { value, date: entries[idx].date }
      }
    }
  })

  for (let idx = 1; idx < values.length; idx++) {
    const prev = values[idx - 1]
    const next = values[idx]
    if (typeof prev !== 'number' || typeof next !== 'number' || prev <= 0 || next <= 0) continue
    const x1 = (idx - 1) * step
    const x2 = idx * step
    const y1 = getLatencyY(prev)
    const y2 = getLatencyY(next)
    const color = getLatencyColor(Math.max(prev, next))
    segments.push({ d: `M${x1.toFixed(2)},${y1.toFixed(2)} L${x2.toFixed(2)},${y2.toFixed(2)}`, color })
  }

  return { hasData: segments.length > 0, segments, points, spikes, peak }
}

const latencySeriesMap = computed(() => {
  const map = new Map<string, LatencySeries>()
  for (const component of sortedComponents.value) {
    const entries = getComponentHistory(component.id)
    map.set(component.id, buildLatencySeries(entries))
  }
  return map
})

function getLatencySeries(componentId: string) {
  return latencySeriesMap.value.get(componentId) ?? { hasData: false, segments: [], points: [], spikes: [], peak: null }
}

function formatHistoryTooltip(point: ComponentHistoryDisplayPoint) {
  if (point.uptime === null) return `${formatDate(point.date)} • No data`
  const uptimeLabel = `${(point.uptime * 100).toFixed(2)}% uptime`
  const responseLabel = point.avgResponseTimeMs && point.avgResponseTimeMs > 0
    ? `${point.avgResponseTimeMs.toFixed(0)}ms avg`
    : 'avg response —'
  const maxLabel = point.maxResponseTimeMs && point.maxResponseTimeMs > 0
    ? `max ${point.maxResponseTimeMs.toFixed(0)}ms`
    : null
  const checksLabel = point.totalChecks > 0
    ? `${point.failedChecks} failed / ${point.totalChecks} checks`
    : 'no checks'
  const incidentLabel = `${point.incidents} incident${point.incidents === 1 ? '' : 's'}`
  const latencyLabel = maxLabel ? `${responseLabel} • ${maxLabel}` : responseLabel
  return `${formatDate(point.date)} • ${uptimeLabel} • ${latencyLabel} • ${checksLabel} • ${incidentLabel}`
}

function formatUptime(value: number | null, fallback = '—') {
  if (value === null || Number.isNaN(value)) return fallback
  return `${(value * 100).toFixed(2)}%`
}

function formatResponseTime(value: number | null) {
  if (value === null || Number.isNaN(value)) return '—'
  return `${value.toFixed(0)}ms`
}

function formatLatencyValue(value: number) {
  if (value >= 1000) {
    const seconds = value / 1000
    return `${Number.isInteger(seconds) ? seconds.toFixed(0) : seconds.toFixed(1)}s`
  }
  return `${value.toFixed(0)}ms`
}

function formatLatencyAxisLabel(value: number) {
  return formatLatencyValue(value)
}

function formatLatencyPeakLabel(peak: LatencyPeak | null) {
  if (!peak) return ''
  return `Peak: ${formatLatencyValue(peak.value)} on ${formatDateShort(peak.date)}`
}

function formatLatencyTooltip(point: LatencyPoint, component: any) {
  const avg24h = getComponentResponseTime24h(component)
  const avg24hLabel = avg24h ? `24h avg ${formatLatencyValue(avg24h)}` : null
  const maxLabel = point.maxResponseTimeMs ? `max ${formatLatencyValue(point.maxResponseTimeMs)}` : null
  const severityLabel = point.severity === 'critical'
    ? 'Severe latency detected'
    : point.severity === 'warning'
      ? 'High latency detected'
      : null

  return [
    `${formatDate(point.date)} • ${formatLatencyValue(point.value)} avg`,
    avg24hLabel,
    maxLabel,
    severityLabel
  ].filter(Boolean).join(' • ')
}

function formatIncidentCount(value: number | null) {
  if (value === null || Number.isNaN(value)) return '—'
  return String(value)
}

function incidentSeverityDot(severity: string | number) {
  const sev = typeof severity === 'string' ? severity.toLowerCase() : severity
  if (sev === 3 || sev === 'critical') return 'bg-red-500'
  if (sev === 2 || sev === 'major') return 'bg-orange-500'
  if (sev === 1 || sev === 'minor') return 'bg-amber-400'
  return 'bg-gray-400'
}

function formatStatusLabel(status: unknown) {
  if (status === null || status === undefined) return ''
  const label = typeof status === 'string' ? status : String(status)
  return label.replace(/_/g, ' ')
}

async function subscribe() {
  if (!subscriptionEmail.value || !statusPage.value?.slug) return

  subscribing.value = true
  try {
    const apiBase = resolveApiBase()

    // Simple pacing: ensure at least 800ms since page load to reduce bot noise
    const elapsed = Date.now() - pageLoadStartedAt
    if (elapsed < 800) {
      await new Promise(resolve => setTimeout(resolve, 800 - elapsed))
    }

    // Exchange page session token for subscription token (one-time/short-lived)
    if (!subscriptionToken.value) {
      const tokenUrl = `${apiBase}/public/status/${statusPage.value.slug}/subscription-token`
      const tokenResponse = await fetch(tokenUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Original-Host': originalHost },
        body: JSON.stringify({
          pageSessionToken: pageSessionToken.value,
          honeypot: honeypot.value
        })
      })
      if (!tokenResponse.ok) {
        throw new Error('Unable to start subscription. Please reload and try again.')
      }
      const tokenBody = await tokenResponse.json()
      subscriptionToken.value = tokenBody.subscriptionToken || null
    }

    const apiUrl = `${apiBase}/public/status/${statusPage.value.slug}/subscribe`
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Original-Host': originalHost,
        ...(subscriptionToken.value ? { 'X-Status-Token': subscriptionToken.value } : {})
      },
      body: JSON.stringify({
        email: subscriptionEmail.value,
        honeypot: honeypot.value,
        subscriptionToken: subscriptionToken.value,
        components: [],
        preferences: {
          incidents: true,
          maintenances: true,
          emailNotifications: true,
          smsNotifications: false,
          components: []
        }
      })
    })

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`)
    }

    subscriptionEmail.value = ''
    alert('Successfully subscribed!')
  } catch (err) {
    console.error('Subscription error:', err)
    alert('Failed to subscribe. Please try again.')
  } finally {
    subscribing.value = false
  }
}

async function loadHistory(days = loadedHistoryDays.value) {
  if (!statusPage.value?.slug) return
  historyLoading.value = true
  historyError.value = false
  try {
    const apiBase = resolveApiBase()
    const apiUrl = `${apiBase}/public/status/${statusPage.value.slug}/history?days=${days}`
    const response = await fetch(apiUrl, { headers: { 'X-Original-Host': originalHost }, cache: 'no-store' })
    if (response.ok) {
      historyData.value = await response.json()
      loadedHistoryDays.value = days
    } else {
      historyError.value = true
    }
  } catch (err) {
    historyError.value = true
    console.error('Failed to load history', err)
  } finally {
    historyLoading.value = false
  }
}

async function loadPastIncidents(loadMore = false) {
  if (!statusPage.value?.slug || pastIncidentsLoading.value) return
  pastIncidentsLoading.value = true
  try {
    const page = loadMore ? pastIncidentsPage.value + 1 : 1
    const apiBase = resolveApiBase()
    const apiUrl = `${apiBase}/public/status/${statusPage.value.slug}/incidents?days=${selectedRangeDays.value}&page=${page}&pageSize=10`
    const response = await fetch(apiUrl, { headers: { 'X-Original-Host': originalHost }, cache: 'no-store' })
    if (!response.ok) throw new Error('Failed to fetch incidents')
    const incidents = await response.json()
    if (loadMore) {
      pastIncidentsPage.value = page
      pastIncidents.value.push(...incidents)
    } else {
      pastIncidentsPage.value = 1
      pastIncidents.value = incidents
    }
    pastIncidentsHasMore.value = incidents.length === 10
  } catch (err) {
    console.error('Failed to load past incidents:', err)
    pastIncidentsHasMore.value = false
  } finally {
    pastIncidentsLoading.value = false
  }
}

function loadMorePastIncidents() {
  if (!pastIncidentsHasMore.value || pastIncidentsLoading.value) return
  loadPastIncidents(true)
}

function selectHistoryRange(days: number) {
  if (selectedRangeDays.value === days) return
  selectedRangeDays.value = days
  pastIncidentsHasMore.value = true
  loadPastIncidents(false)
  if (days > loadedHistoryDays.value) {
    loadHistory(days)
  }
}

function onRangeSelectChange(event: Event) {
  const target = event.target as HTMLSelectElement | null
  if (!target) return
  const value = Number(target.value)
  if (!Number.isNaN(value)) {
    selectHistoryRange(value)
  }
}

function resolveApiBase(): string {
  const envBase = (import.meta as any).env?.VITE_PUBLIC_STATUS_API_BASE as string | undefined
  if (envBase) return envBase.replace(/\/$/, '')
  const host = window.location.hostname
  if (host === 'localhost' || host === '127.0.0.1') {
    return 'http://localhost:5010/api'
  }
  return `${window.location.origin.replace(/\/$/, '')}/api`
}

function buildStatusUrl(passwordParam?: string) {
  const apiBase = resolveApiBase()
  let apiUrl = `${apiBase}/public/status`

  const host = window.location.hostname
  if (host !== 'localhost' && host !== '127.0.0.1' && !host.includes('3001')) {
    const parts = host.split('.')
    if (parts.length >= 3 && parts[1] === 'pingalert' && parts[2] === 'live') {
      apiUrl += `/subdomain/${parts[0]}`
    } else {
      apiUrl += '/by-domain'
    }
  } else if (route.params.slug) {
    apiUrl += `/${route.params.slug as string}`
  } else {
    apiUrl += '/demo-status'
  }

  if (passwordParam) {
    apiUrl += `?password=${encodeURIComponent(passwordParam)}`
  }

  return apiUrl
}

async function loadStatusPage(passwordParam?: string) {
  try {
    const apiUrl = buildStatusUrl(passwordParam)
    const response = await fetch(apiUrl, { headers: { 'X-Original-Host': originalHost }, cache: 'no-store' })

    if (response.status === 401) {
      showPasswordPrompt.value = true
      return
    }

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`)
    }

    const body = await response.json()
    pageSessionToken.value = body.pageSessionToken || null
    subscriptionToken.value = null
    statusPage.value = body
    document.title = `${statusPage.value.name} | Status`
    updateFavicon()
    await Promise.all([loadHistory(), loadPastIncidents()])
  } catch (err) {
    error.value = true
    console.error('Failed to load status page:', err)
  } finally {
    loading.value = false
  }
}

async function authenticate() {
  if (!password.value) return
  passwordError.value = ''
  authenticating.value = true
  try {
    await loadStatusPage(password.value)
    if (statusPage.value) {
      showPasswordPrompt.value = false
      password.value = ''
    } else {
      passwordError.value = 'Invalid password'
    }
  } catch (err) {
    passwordError.value = 'Authentication failed'
    console.error('Authentication error:', err)
  } finally {
    authenticating.value = false
  }
}

function scrollToSubscribe() {
  subscriptionSection.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

function updateFavicon() {
  if (statusPage.value?.branding?.faviconUrl) {
    const favicon = document.querySelector("link[rel='icon']") as HTMLLinkElement
    if (favicon) favicon.href = statusPage.value.branding.faviconUrl
  }
}

onMounted(() => {
  loadStatusPage()
})
</script>

<style scoped>
:global(body) {
  background-color: #ffffff;
}

.prose :where(img) {
  display: inline-block;
}
</style>
